/**
 * Pure utility functions extracted from MainDashboard — no Vue reactivity dependencies.
 */

export function resolveDashboardMode(rawMode) {
  return rawMode === 'edit' ? 'edit' : 'view'
}

export function hasAlertEntries(alerts) {
  if (!Array.isArray(alerts)) return false
  return alerts.some((alert) => {
    if (typeof alert === 'string') return alert.trim().length > 0
    return alert != null
  })
}

export function alertSeverityForUnit(unitData) {
  if (!unitData || typeof unitData !== 'object') return null
  const alerts = unitData.alerts
  if (!alerts || typeof alerts !== 'object') return null
  if (hasAlertEntries(alerts.critical)) return 'critical'
  if (hasAlertEntries(alerts.warning)) return 'warning'
  if (hasAlertEntries(alerts.info)) return 'info'
  return null
}

export function toMetricLabel(field = '') {
  return String(field || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function flattenTimeseriesStyleSpecs(specifications) {
  if (!specifications || typeof specifications !== 'object' || Array.isArray(specifications)) return []
  const flattened = []
  for (const [source, lines] of Object.entries(specifications)) {
    if (!Array.isArray(lines)) continue
    for (const line of lines) {
      if (!line?.field) continue
      flattened.push({
        source,
        field: line.field,
        friendlyName: line.friendlyName || line.label || '',
        unit: line.unit || '',
      })
    }
  }
  return flattened
}

export function dottedSnapshotSpecs(rawSpecs) {
  if (!rawSpecs || typeof rawSpecs !== 'object' || Array.isArray(rawSpecs)) return []
  const specs = []
  for (const key of Object.keys(rawSpecs)) {
    if (!key.includes('.')) continue
    const splitIdx = key.indexOf('.')
    const source = key.slice(0, splitIdx).trim()
    const field = key.slice(splitIdx + 1).trim()
    if (!source || !field) continue
    const snapshotValue = rawSpecs[key]
    specs.push({
      source,
      field,
      friendlyName:
        typeof snapshotValue?.friendlyName === 'string' && snapshotValue.friendlyName.trim()
          ? snapshotValue.friendlyName
          : toMetricLabel(field),
      unit: typeof snapshotValue?.unit === 'string' ? snapshotValue.unit : '',
      mapAnimationRule:
        snapshotValue?.mapAnimationRule &&
        typeof snapshotValue.mapAnimationRule === 'object' &&
        !Array.isArray(snapshotValue.mapAnimationRule)
          ? { ...snapshotValue.mapAnimationRule }
          : null,
    })
  }
  return specs
}

export function normalizeQuickMetricSpecs(rawSpecs) {
  if (!Array.isArray(rawSpecs)) {
    return dottedSnapshotSpecs(rawSpecs)
  }
  return rawSpecs
    .map((spec) => ({
      source:
        typeof spec === 'string'
          ? spec.includes('.')
            ? spec.slice(0, spec.indexOf('.')).trim()
            : ''
          : typeof spec?.source === 'string'
          ? spec.source
          : '',
      field:
        typeof spec === 'string'
          ? spec.includes('.')
            ? spec.slice(spec.indexOf('.') + 1).trim()
            : ''
          : typeof spec?.field === 'string'
          ? spec.field
          : '',
      friendlyName:
        typeof spec?.friendlyName === 'string'
          ? spec.friendlyName
          : typeof spec?.label === 'string'
          ? spec.label
          : typeof spec === 'string'
          ? toMetricLabel(spec.includes('.') ? spec.slice(spec.indexOf('.') + 1) : spec)
          : '',
      unit: typeof spec?.unit === 'string' ? spec.unit : '',
      mapAnimationRule:
        spec?.mapAnimationRule &&
        typeof spec.mapAnimationRule === 'object' &&
        !Array.isArray(spec.mapAnimationRule)
          ? { ...spec.mapAnimationRule }
          : null,
    }))
    .filter((spec) => spec.field)
}
